import deepEqual from "fast-deep-equal";
import { Atom, atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { Page } from "../types";
import {
  extractAssetId,
  extractInterfacePageId,
  isTemplateInterfacePageId,
} from "../utils";
import { templateAtom } from "../templates";
import { aiSuggestedEditsAtomFamily } from "../nodes";

type Params = {
  nodeId: string;
  interfaceProjectId: string | undefined;
  interfacePageId: string | undefined;
  proposalId?: string | undefined;
};

export const interfacePageAsyncAtomFamily = atomFamily(
  ({
    nodeId,
    interfaceProjectId,
    interfacePageId,
    proposalId,
  }: Params): Atom<Promise<Page | null>> => {
    const interfacePageAtom: Atom<Promise<Page | null>> = atom(async (get) => {
      if (!interfacePageId && !proposalId) {
        return null;
      }

      // if proposalId is provided, we are using the ai suggested edits and it is a template interface page
      const isTemplateInterfacePage =
        isTemplateInterfacePageId(interfacePageId) || proposalId;

      if (isTemplateInterfacePage) {
        const aiProposedEdits = get(aiSuggestedEditsAtomFamily(nodeId));
        const zslTemplate = get(templateAtom);

        if (aiProposedEdits && proposalId) {
          const aiProposed = aiProposedEdits.additions.find(
            (addition) => addition.id === proposalId,
          );

          if (aiProposed) {
            if (aiProposed.zsl?.interfaces) {
              // Find the page in the proposed interface
              const page = aiProposed.zsl.interfaces[0].pages.find(
                (p: Page) => p.id === interfacePageId,
              );
              if (page) {
                return page;
              }
            }
            if (aiProposed?.outline?.interfaces?.[0]?.pages) {
              // Try finding the page in the outline

              const page = aiProposed?.outline.interfaces[0].pages.find(
                (p: Page) => p.id === interfacePageId,
              );
              if (page) {
                return page;
              }
            }
          }
        }

        // Then check template
        if (zslTemplate && zslTemplate.interfaces && interfacePageId) {
          const coreInterfaceProjectId = extractAssetId(
            interfaceProjectId ?? "",
          );

          const interfaceTemplate = zslTemplate.interfaces.find(
            (project) => coreInterfaceProjectId === project.id,
          );

          if (interfaceTemplate && interfaceTemplate.pages) {
            const corePageId = extractInterfacePageId(interfacePageId);

            const pageTemplate = interfaceTemplate.pages.find(
              (page) => corePageId === page.id,
            );

            if (pageTemplate) {
              return pageTemplate as Page;
            }
          }
        }

        // Because we don't want to fetch with template asset id and get a 404 Not Found error
        return null;
      }

      try {
        const res = await fetch(
          `/api/canvasui/interfaces/pages/${interfacePageId}`,
        );

        if (res.ok) {
          const {
            result: { data },
          } = await res.json();

          return {
            blocks: data.content.blocks,
            name: data.name,
            id: data.id,
            slug: data.slug,
          } as Page;
        } else {
          console.error("Failed to fetch interface page:", res.statusText);
          return null;
        }
      } catch (error) {
        console.error("Error fetching interface page:", error);
        return null;
      }
    });

    interfacePageAtom.debugLabel = `interfacePageAsyncAtomFamily(${interfaceProjectId}-${interfacePageId}-nodeId:${nodeId})`;
    return interfacePageAtom;
  },
  deepEqual,
);
